/*
 * Carbisdale - OutlineView
 *
 * Rendering-Modul für den Outline-Pane.
 * Zeigt die Gliederungsstruktur zeilen-synchron mit dem Editor.
 * Rendert Note-Text mit >< Markern nach den Outline-Spalten.
 *
 * Die Outline-Spalten werden in EditorView._rebuildLines() synchron
 * mit den Zeilen berechnet und hier nur gerendert. Cursor-Highlighting
 * wird erst beim Rendern aufgelöst (dual-Style: style/highlightStyle).
 */

import { Style, EDITOR, LINE_HEIGHT_Q } from '../config.js'
import { parsedToFormatRuns, parsedCharsToString, charDisplayWidth } from '../TextProcessor.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { pickMarkerStyle, pickSeparatorMarkerStyle, MarkerPane } from './activeFieldHighlight.js'
import { enrichRunsWithSpellWarnings } from './spellWarningEnrich.js'

/**
 * OutlineView-Klasse
 * Rendert die Outline-Struktur und Note-Text im Outline-Pane.
 */
export class OutlineView {
    /**
     * @param {EditorModel} model - Das Datenmodell
     * @param {EditorView} editorView - Die Editor-View (für Zeilen-Synchronisation)
     */
    constructor(model, editorView) {
        this.model = model
        this.editorView = editorView

        // Letzter aktueller Absatz (für Scroll-Shift: Highlighting-Wechsel erkennen)
        this._lastCurrentParagraphId = null

        // Letztes Cursor-Feld — Wechsel (text↔note↔info) verschiebt das
        // Marker-Highlight zwischen Panes (siehe activeFieldHighlight). Wird
        // im Cursor-Only-Pfad geprueft, damit alle Panes neu malen.
        this._lastCursorField = null

        // Hover-State für Separator-Controls
        this._hoveredSeparatorControl = null  // { type, paragraphId } oder null
        // Hover-State für das Collapse-Icon eines Outline-Items
        this._hoveredOutlineItemIcon = null   // paragraphId oder null
    }

    /**
     * Berechnet den Y-Shift für Marker-Zeichen ((-)  > etc.) in Zeilen-Einheiten.
     * Marker sind 4QL-Grid-Zeichen, die an der 6QL-Note-Baseline ausgerichtet werden:
     * 1. Basis-Shift: 1QL runter (4QL-Zeichen in 6QL-Zeile zentrieren)
     * 2. Heading-Shift: Baseline-Alignment für größere Zeilen (gleiche Formel wie Main)
     * @param {number} heightQ - Zeilenhöhe in Quarter-Lines
     * @returns {number} Y-Shift in Zeilen-Einheiten (für fractionalY-Addition)
     */
    _markerShiftY(heightQ) {
        const renderer = this.editorView.renderer
        if (!renderer || !renderer.isProportional() || heightQ <= 4) return 0
        const MARKER_HQ = 6
        // Basis: 4QL-Zeichen in 6QL-Zeile → 1QL runter. Das Grid erlaubt keine
        // perfekte Ausrichtung zur 6QL Note-Baseline, aber so sitzen Icon und
        // Connectors zueinander sauber und insgesamt optisch am nächsten dran.
        const baseShift = 0.25
        // Heading-Extra: Baseline-Shift für Zeilen größer als 6QL
        const headingShift = heightQ > MARKER_HQ
            ? (renderer.fontSizeForHeightQ(heightQ) - renderer.fontSizeForHeightQ(MARKER_HQ)) * 0.85 / renderer.charHeight
            : 0
        return baseShift + headingShift
    }

    /**
     * Vertikaler Feinschliff-Offset des Collapse-Icons in Zeilen-Einheiten. Der
     * Note-Text sitzt um secondaryBaselineShiftPx(mainHeightQ) tiefer (Ausrichtung
     * an der co-located Main-Zeile). Das Icon folgt diesem Shift: der konstante
     * OUTLINE_NOTE_ICON_Y_OFFSET_Q ist auf den NORMAL-Shift kalibriert, die Differenz
     * zum tatsächlichen Shift wird hier verrechnet. So fluchtet das Icon auch bei
     * kleinerer Main-Höhe (Outline maximiert → Note top-aligned, mainHeightQ < NORMAL)
     * mit dem Note-Text, statt 1QL zu tief zu sitzen. Normal-Modus (mainHeightQ =
     * NORMAL) bleibt exakt unverändert (delta = 0).
     * @param {number} mainHeightQ  heightQ der Main-Zeile dieser Outline-Zeile
     * @returns {number} Offset in Zeilen-Einheiten
     */
    _iconBaselineOffsetLines(mainHeightQ) {
        const ev = this.editorView
        const base = EDITOR.OUTLINE_NOTE_ICON_Y_OFFSET_Q / 4
        const ch = ev.renderer ? ev.renderer.charHeight : 0
        if (!ch || !ev._fieldBaselineShiftPx) return base
        const delta = (ev._fieldBaselineShiftPx(mainHeightQ)
            - ev._fieldBaselineShiftPx(LINE_HEIGHT_Q.NORMAL)) / ch
        return base + delta
    }

    /**
     * Berechnet den Baseline-Shift in Pixeln für Note-Marker und Note-Text.
     * Note-Inhalt wird in 4QL (SECONDARY, System-Font) gerendert — bei größeren
     * Hauptzeilen muss er per Baseline verschoben werden (gleiche Formel wie
     * Absatzmarker in Main).
     * @param {number} mainHeightQ - heightQ der zugehörigen Hauptzeile
     * @returns {number} Pixel-Shift
     */
    _noteBaselineShiftPx(mainHeightQ) {
        const renderer = this.editorView.renderer
        if (!renderer || !renderer.isProportional()) return 0
        return renderer.secondaryBaselineShiftPx(mainHeightQ)
    }

    /**
     * Schiebt Tree-Linien-Chars (│├┤└┘) als Linien-Segmente in die Batch-Queues,
     * statt sie per setCharFn als Atlas-lose Live-Strokes zu zeichnen. Damit
     * laufen Arms und Box-Char durchgehend als EIN Path pro Style — kein Glow-
     * Bruch mehr an den Gelenken im Highlighted-State.
     *
     * @returns {boolean} true wenn der Char gebatched wurde (Aufrufer überspringt
     *                    setCharFn), false bei Nicht-Tree-Chars
     */
    _pushTreeCharSegments(char, col, cellTopPx, cellBotPx, cy, cw, styleName, verts, horiz, cornerDropPx = 0) {
        const fullVert = (char === '│' || char === '├' || char === '┤')
        const halfVert = (char === '└' || char === '┘')
        if (!fullVert && !halfVert) return false

        // └/┘-Eckpunkt darf gegenüber der Zeilen-Mitte abgesenkt werden
        // (cornerDropPx, Boris-Wunsch: 1QL tiefer). Betrifft nur die Eck-Chars,
        // nicht die durchlaufenden ├┤ (deren Stummel sitzt auf der Zeilen-Mitte).
        const cornerY = halfVert ? cy + cornerDropPx : cy

        if (fullVert) {
            // Durchgehende Vertikale über die ganze Zeile — absorbiert Arm-up,
            // Box-Char-Mittellinie und Arm-down in EINEM Segment
            verts.push({ col, fromPx: cellTopPx, toPx: cellBotPx, styleName })
        } else {
            // └ und ┘: nur obere Hälfte bis zum (ggf. abgesenkten) Eckpunkt,
            // unterhalb ist die Zelle leer (kein Arm-down)
            verts.push({ col, fromPx: cellTopPx, toPx: cornerY, styleName })
        }

        // Horizontaler Stummel ins Nachbarfeld (halbe Zellbreite)
        const xLeft = col * cw
        if (char === '├' || char === '└') {
            horiz.push({ x0: xLeft + cw / 2, x1: xLeft + cw, y: cornerY, styleName })
        } else if (char === '┤' || char === '┘') {
            horiz.push({ x0: xLeft, x1: xLeft + cw / 2, y: cornerY, styleName })
        }

        return true
    }

    /**
     * Brücke vom (-)/(+)-Icon zur Fortsetzungs-Linie darunter. Das Icon zeichnet
     * selbst keine Linie; läuft der Outline-Knoten unter dem Icon weiter (Buddy
     * umgebrochen ODER Kind-Absätze), muss eine Vertikale vom UNTEREN ICON-RAND
     * bis zur Slot-Unterkante dieser Zeile gezogen werden. Wichtig: Startpunkt ist
     * die echte Icon-Mitte + Radius (nicht die treibende Zeilen-Mitte `cy` — die
     * liegt bei Umbruch über dem Icon → Linie schnitt ins Icon; bei getrennten
     * Absätzen wurde gar nichts gezeichnet → Lücke).
     *
     * Fortsetzung erkannt über die Spalten der Folge-Zeile (gleiche Connector-
     * Spalte): deckt Umbruch (gleicher Slot) UND Kind-Absatz (Folge-Slot) ab.
     *
     * @param {number} centerColGrid - Grid-Spalte des Icon-Zentrums (mit Indent)
     * @param {number} offsetX - Pane-X-Offset
     * @param {number} iconY - Grid-Zeile des Icons (wie an _renderControlIcon)
     * @param {number} cellBotPx - Slot-Unterkante dieser Zeile im treibenden Grid
     * @param {number} lineIndex - Index der Icon-Zeile
     * @param {string} styleName - Connector-Style
     * @param {Array} connectors - Ziel-Queue
     */
    _pushIconBridge(centerColGrid, offsetX, iconY, cellBotPx, lineIndex, styleName, connectors) {
        const r = this.editorView.renderer
        // Icon-Unterkante EXAKT wie _renderControlIcon (über Renderer-Helper) —
        // nicht die treibende Zeilen-Mitte (die liegt bei Umbruch über dem Icon →
        // Linie schnitt rein) und nicht eine eigene Radius-Formel.
        const iconBottomPx = r.controlIconGeometry
            ? r.controlIconGeometry(iconY, EDITOR.OUTLINE_NOTE_ICON_SCALE).bottomY
            : (iconY * r.charHeight + r.charHeight / 2)

        // Läuft der Knoten unter dem Icon weiter? Folge-Zeile mit Connector-Char
        // an derselben Spalte (│├┤└┘) → ja. Deckt Umbruch (gleicher Slot) UND
        // getrennten Kind-Absatz (Folge-Slot) ab.
        const nextCols = this.editorView._outlineColumns.get(lineIndex + 1)
        const continues = !!nextCols && nextCols.some(
            c => c.col === centerColGrid && '│├┤└┘'.includes(c.char)
        )
        if (!continues) return
        if (iconBottomPx >= cellBotPx) return

        connectors.push({
            col: offsetX + centerColGrid,
            fromPx: iconBottomPx,
            toPx: cellBotPx,
            styleName
        })
    }

    /**
     * Rendert den sichtbaren Outline-Bereich.
     * Zeilen-synchron mit dem Editor (gleicher viewportStart).
     *
     * @param {number} offsetX - Absoluter X-Offset des Outline-Pane
     * @param {number} offsetY - Absoluter Y-Offset
     * @param {number} width - Verfügbare Breite
     * @param {number} height - Verfügbare Höhe
     * @param {Function} setCharFn - Funktion (x, y, char, style)
     */
    /**
     * Liefert den passenden Marker-Style fuer den Outline-Pane:
     * EDITOR_MARKER fuer den aktiven Absatz im note-Feld, sonst EDITOR_MARKER_DIM.
     * @param {*} paragraphId
     * @returns {string}
     */
    _markerStyle(paragraphId) {
        return pickMarkerStyle(MarkerPane.OUTLINE, paragraphId, this.model.cursor)
    }

    render(offsetX, offsetY, width, height, setCharFn) {
        this._lastCurrentParagraphId = this.model.cursor ? this.model.cursor.paragraphId : null
        this._lastCursorField = this.model.cursor ? (this.model.cursor.field || 'text') : null
        if (this.editorView._useQuarterSpacing() && this.editorView.renderer) {
            this._renderLinesQuarterSpacing(offsetX, offsetY, width, height, setCharFn)
        } else {
            this._renderLines(offsetX, offsetY, width, height, 0, height, setCharFn)
        }
    }

    /**
     * Rendert nur die neu sichtbar gewordenen Outline-Zeilen nach einem Scroll-Shift.
     */
    renderPartial(offsetX, offsetY, width, height, scrollDelta, setCharFn, shiftPixels = 0) {
        // Wenn sich der aktuelle Absatz oder das Feld (text↔note↔info) geaendert
        // hat, muessen alle sichtbaren Zeilen neu gerendert werden — Active-Field-
        // Highlight (Marker dim/voll) und Outline-Current-Style aendern sich global.
        const currentParagraphId = this.model.cursor ? this.model.cursor.paragraphId : null
        const currentField = this.model.cursor ? (this.model.cursor.field || 'text') : null
        if (currentParagraphId !== this._lastCurrentParagraphId
                || currentField !== this._lastCursorField) {
            this._lastCurrentParagraphId = currentParagraphId
            this._lastCursorField = currentField
            if (this.editorView._useQuarterSpacing() && this.editorView.renderer) {
                // Quarter-Spacing: alle sichtbaren Zeilen mit fraktionalen Positionen
                this._renderLinesForRange(offsetX, offsetY, width, height,
                    this.editorView.viewportStart, this.editorView.lines.length, setCharFn)
            } else {
                this._renderLines(offsetX, offsetY, width, height, 0, height, setCharFn)
            }
            return
        }

        // Sub-Pixel-only-Frame (Proportional): nichts zu rendern, Canvas bleibt unverändert.
        if (this.editorView.renderer && shiftPixels === 0 && this.editorView._useQuarterSpacing()) {
            return
        }

        if (this.editorView.renderer && shiftPixels !== 0) {
            // Pixel-basiert — Proportional UND Monospace (beide mit Sub-Pixel-Scroll)
            const ev = this.editorView
            const useQuarter = ev._useQuarterSpacing() && ev._lineQuarterY.length > 0
            const ch = ev.renderer.charHeight
            const viewportPx = height * ch
            const absShift = Math.abs(shiftPixels)
            const exposedStart = shiftPixels > 0 ? viewportPx - absShift : 0
            const exposedEnd = shiftPixels > 0 ? viewportPx : absShift

            // Zeilen finden die im exponierten Bereich liegen. Über BEIDE Grids
            // (treibendes Connector-Pane + Note), sonst reißt beim Scroll-Shift der
            // Connector ab, wenn seine Note-Zeile kollabiert (alle auf slotBottom)
            // aber die Connector-Vertikale im treibenden Grid noch im Band liegt.
            const startLine = useQuarter
                ? Math.min(ev._firstVisibleLine('text'), ev._firstVisibleLine('note'), ev._firstVisibleLine('info'))
                : ev._firstVisibleLine('note')
            let from = -1, to = -1
            for (let i = startLine; i < ev.lines.length; i++) {
                const drvField = useQuarter ? ev._outlineConnectorFieldAt(i) : 'note'
                const connTop = ev._lineScreenPixelY(i, drvField)
                const noteTop = ev._lineScreenPixelY(i, 'note')
                if (connTop >= viewportPx && noteTop >= viewportPx) break
                const connH = useQuarter ? (ev._paneCellHeightQ(drvField, i) * (ch / 4)) : ch
                const noteH = useQuarter ? (ev._paneCellHeightQ('note', i) * (ch / 4)) : ch
                const top = Math.min(connTop, noteTop)
                const bot = Math.max(connTop + connH, noteTop + noteH)
                if (bot > exposedStart && top < exposedEnd) {
                    if (from < 0) from = i
                    to = i + 1
                }
            }
            if (from >= 0) {
                this._renderLinesForRange(offsetX, offsetY, width, height, from, to, setCharFn)
            }
        } else if (scrollDelta > 0) {
            // Nach unten gescrollt: neue Zeilen am unteren Rand
            const startY = height - scrollDelta
            this._renderLines(offsetX, offsetY, width, height, startY, height, setCharFn)
        } else {
            // Nach oben gescrollt: neue Zeilen am oberen Rand
            this._renderLines(offsetX, offsetY, width, height, 0, Math.abs(scrollDelta), setCharFn)
        }
    }

    /**
     * Berechnet die Start-Spalte für Note-Text eines bestimmten Paragraphs.
     * Delegiert an EditorView für per-Paragraph Berechnung.
     * @param {number} paragraphId
     * @returns {number} Spalte wo der > Marker beginnt
     */
    getNoteStartCol(paragraphId) {
        return this.editorView.getNoteStartCol(paragraphId)
    }

    /**
     * Rendert nur die dirty Zeilen-Ranges im Outline (für Paragraph-Partial-Pfad).
     * Die Ranges kommen vom EditorView.tryRenderDirtyOnly() und sind identisch
     * für Main und Outline (synchronisierte Zeilen-Indizes).
     *
     * @param {number} offsetX - Outline-Pane X-Offset
     * @param {number} offsetY - Outline-Pane Y-Offset
     * @param {number} width - Outline-Pane Breite
     * @param {number} height - Outline-Pane Höhe
     * @param {Array} dirtyRanges - [{fromLineIndex, toLineIndex}]
     * @param {Function} setCharFn - Render-Callback
     * @param {Function} clearLineFn - (charX, charY, charWidth)
     */
    renderDirtyRanges(offsetX, offsetY, width, height, dirtyRanges, setCharFn, clearLineFn) {
        this._lastCurrentParagraphId = this.model.cursor ? this.model.cursor.paragraphId : null

        for (const range of dirtyRanges) {
            const fromY = this.editorView._lineToScreenY(range.fromLineIndex, 'note')
            const toY = this.editorView._lineToScreenY(range.toLineIndex, 'note')

            // Dirty Zeilen clearen und neu rendern
            if (this.editorView._useQuarterSpacing()) {
                // Proportional: fraktionale Y → Clear und Render per lineIndex (Note-Grid)
                for (let li = range.fromLineIndex; li < range.toLineIndex; li++) {
                    const screenY = this.editorView._lineToScreenY(li, 'note')
                    const heightQ = this.editorView._paneCellHeightQ('note', li)
                    clearLineFn(offsetX, offsetY + screenY, width, heightQ)
                }
                this._renderLinesForRange(offsetX, offsetY, width, height,
                    range.fromLineIndex, range.toLineIndex, setCharFn)
            } else {
                for (let y = fromY; y < toY; y++) {
                    clearLineFn(offsetX, offsetY + y, width)
                }
                this._renderLines(offsetX, offsetY, width, height, fromY, toY, setCharFn)
            }
        }
    }

    /**
     * Rendert einen Bereich von Outline-Zeilen (Struktur + Notes).
     * Liest Outline-Spalten direkt aus editorView._outlineColumns (immer synchron).
     * Cursor-Highlighting wird hier aufgelöst über dual-Styles.
     */
    _renderLines(offsetX, offsetY, width, height, fromY, toY, setCharFn) {
        const viewportStart = this.editorView.viewportStart
        const outlineColumns = this.editorView._outlineColumns
        const noteLines = this.editorView.noteLines
        const outlinePaneWidth = this.editorView.outlinePaneWidth
        const currentParagraphId = this._lastCurrentParagraphId

        if (!outlineColumns) return

        for (let y = fromY; y < toY; y++) {
            const lineIndex = viewportStart + y

            if (lineIndex >= this.editorView.lines.length) break

            // Separator-Zeile: strukturierte Zeile mit Tiefe, Nummer und Controls
            const mainLine = this.editorView.lines[lineIndex]
            if (mainLine.isSeparator) {
                this._renderSeparatorLine(
                    offsetX, offsetY + y, width,
                    mainLine, setCharFn
                )
                continue
            }

            // Background-Suffix für Styles im Background-Bereich
            const bgSuffix = mainLine.isBackground ? '+BG' : ''

            // Outline-Struktur rendern (mit Cursor-Highlighting-Auflösung)
            const lineColumns = outlineColumns.get(lineIndex)
            if (lineColumns && lineColumns.length > 0) {
                const isCurrent = this._isOutlineNodeCurrent(lineIndex, currentParagraphId)
                const renderer = this.editorView.renderer
                const isPropOutline = renderer && renderer.isProportional()

                for (let ci = 0; ci < lineColumns.length; ci++) {
                    const col = lineColumns[ci]
                    if (col.col >= width) continue

                    // Proportional Mode: (-)  Marker als Kreis-Icon rendern
                    if (isPropOutline && col.char === '(' && ci + 2 < lineColumns.length) {
                        const centerCol = lineColumns[ci + 1]
                        const closeCol = lineColumns[ci + 2]
                        if ((centerCol.char === '-' || centerCol.char === '+') && closeCol.char === ')') {
                            const isHovered = this._hoveredOutlineItemIcon === this._outlineItemIdAt(lineIndex)
                            const { borderStyle, symbolStyle } =
                                this._outlineIconStyles(isCurrent, isHovered, bgSuffix)
                            renderer._renderControlIcon(
                                offsetX + centerCol.col, offsetY + y,
                                centerCol.char, symbolStyle, borderStyle
                            )
                            ci += 2  // ( und ) überspringen
                            continue
                        }
                    }

                    // Dual-Style: highlightStyle wenn Absatz aktuell, sonst style
                    const style = ((isCurrent && col.highlightStyle)
                        ? (Style[col.highlightStyle] || Style.OUTLINE_CONNECTOR)
                        : (Style[col.style] || Style.OUTLINE_CONNECTOR)) + bgSuffix
                    setCharFn(
                        offsetX + col.col,
                        offsetY + y,
                        col.char,
                        style
                    )
                }
            }

            // Note-Text rendern (per-Paragraph noteStartCol und noteTextWidth)
            if (outlinePaneWidth > 0 && lineIndex < noteLines.length) {
                const noteLine = noteLines[lineIndex]
                const noteStartCol = this.editorView.getNoteStartCol(noteLine.paragraphId)
                const noteTextWidth = this.editorView.getNoteTextWidth(noteLine.paragraphId)
                if (noteTextWidth > 0) {
                    this._renderNoteLine(
                        offsetX, offsetY + y, width,
                        noteStartCol, noteTextWidth,
                        noteLine, lineIndex,
                        setCharFn, 4
                    )
                }
            }
        }
    }

    /**
     * Rendert alle sichtbaren Outline-Zeilen mit Quarter-Spacing (Proportional-Mode).
     * Iteriert über Zeilen-Indizes statt Screen-Rows, berechnet fraktionale Y-Positionen.
     */
    _renderLinesQuarterSpacing(offsetX, offsetY, width, height, setCharFn) {
        const ev = this.editorView
        const renderer = ev.renderer
        const viewportEndPx = height * renderer.charHeight
        const outlineColumns = ev._outlineColumns
        const noteLines = ev.noteLines
        const outlinePaneWidth = ev.outlinePaneWidth
        const currentParagraphId = this._lastCurrentParagraphId

        if (!outlineColumns) return

        // Connector-Segmente (vertikal + horizontal) sammeln, am Ende als EIN
        // Batch stroken (pro Style einen Path). Tree-Box-Chars │├┤└┘ laufen über
        // diesen Pfad statt live per setCharFn — dadurch durchgehende Linie mit
        // konsistentem Glow im Highlighted-State (Lesson 54).
        const connectors = []
        const horizontals = []
        const cw = renderer.charWidth
        const ch = renderer.charHeight

        const quarterH = ch / 4
        // Start über ALLE Panes: eine sichtbare Connector- oder Note-Zeile kann im
        // treibenden Grid einen niedrigeren Index haben als im Note-Grid.
        const startLine = Math.min(
            ev._firstVisibleLine('text'),
            ev._firstVisibleLine('note'),
            ev._firstVisibleLine('info')
        )
        for (let i = startLine; i < ev.lines.length; i++) {
            const mainLine = ev.lines[i]
            // Connector folgt dem TREIBENDEN Pane, der Note-Text dem Note-Grid.
            // Sichtbarkeit über BEIDE: erst abbrechen, wenn weder Connector noch
            // Note in den Viewport ragen — sonst riss eine kollabierte Note-Padding-
            // Zeile (alle auf slotBottom) die ganze Linie ab, obwohl ihre Main-/
            // Info-Position noch sichtbar war. Beide Grids sind monoton steigend,
            // also bleibt der Break-Abbruch korrekt.
            const drvField = ev._outlineConnectorFieldAt(i)
            const connRelTop = ev._lineScreenPixelY(i, drvField)
            const connCellH = ev._paneCellHeightQ(drvField, i) * quarterH
            const pxY = ev._lineScreenPixelY(i, 'note')
            const noteCellH = ev._paneCellHeightQ('note', i) * quarterH
            if (connRelTop >= viewportEndPx && pxY >= viewportEndPx) {
                // Spacing-Connector der ersten ausserhalb liegenden Zeile noch
                // zeichnen — die vorherige (sichtbare) Zeile hat sonst keinen
                // Baumlinien-Ausläufer über ihre Unterkante hinaus, und beim
                // Scrollen "flattert" das untere Ende, sobald der Folgeabsatz
                // unter den Viewport-Rand rutscht. Clipping kappt den Stroke
                // am Viewport-Rand.
                if (mainLine.spacingBefore > 0 && i > 0) {
                    this._renderSpacingColumns(offsetX, offsetY, width, i, renderer, connectors)
                }
                break
            }
            if (connRelTop + connCellH <= 0 && pxY + noteCellH <= 0) continue

            const fractionalY = pxY / ch

            // Baumlinien durch Spacing-Gap zeichnen (vertikale Verbindungen)
            if (mainLine.spacingBefore > 0 && i > 0) {
                this._renderSpacingColumns(offsetX, offsetY, width, i, renderer, connectors)
            }

            if (mainLine.isSeparator) {
                this._renderSeparatorLine(offsetX, offsetY + fractionalY, width, mainLine, setCharFn)
                continue
            }

            // Background-Suffix
            const bgSuffix = mainLine.isBackground ? '+BG' : ''

            // Outline-Spalten rendern
            const lineColumns = outlineColumns.get(i)
            if (lineColumns && lineColumns.length > 0) {
                const isCurrent = this._isOutlineNodeCurrent(i, currentParagraphId)
                // Outline-Tree liegt im Note-Pane → Marker-Shift nach Note-Höhe (4QL → 0)
                const markerShift = this._markerShiftY(this.editorView.noteLines[i]?.heightQ || 4)
                const isPropQS = renderer.isProportional()

                // Connector-Geometrie folgt dem TREIBENDEN Pane (oben bestimmt):
                // └-Eckpunkt auf der Mitte der letzten echten Zeile, Vertikale über
                // den ganzen Block. cy = echte Zeilen-Mitte statt boxCharCenterY
                // (das gab die 4QL-Zellmitte — war Mechanismus A der Fehldiagnose).
                const cellTopPx = offsetY * ch + connRelTop
                const cellBotPx = cellTopPx + connCellH
                const cy = cellTopPx + connCellH / 2

                for (let ci = 0; ci < lineColumns.length; ci++) {
                    const col = lineColumns[ci]
                    if (col.col >= width) continue

                    // Proportional Mode: (-) Marker als Kreis-Icon
                    if (isPropQS && col.char === '(' && ci + 2 < lineColumns.length) {
                        const centerCol = lineColumns[ci + 1]
                        const closeCol = lineColumns[ci + 2]
                        if ((centerCol.char === '-' || centerCol.char === '+') && closeCol.char === ')') {
                            const isHovered = this._hoveredOutlineItemIcon === this._outlineItemIdAt(i)
                            const { borderStyle, symbolStyle } =
                                this._outlineIconStyles(isCurrent, isHovered, bgSuffix)
                            const iconY = offsetY + fractionalY + markerShift
                                + this._iconBaselineOffsetLines(mainLine.heightQ || 4)
                            renderer._renderControlIcon(
                                offsetX + centerCol.col, iconY,
                                centerCol.char, symbolStyle, borderStyle,
                                EDITOR.OUTLINE_NOTE_ICON_SCALE
                            )
                            // Brücke vom Icon-Unterrand bis zur Slot-Unterkante,
                            // wenn der Knoten weiterläuft (Umbruch ODER Kind-Absatz).
                            this._pushIconBridge(
                                centerCol.col, offsetX, iconY, cellBotPx, i,
                                (isCurrent ? 'OUTLINE_CURRENT' : 'OUTLINE_CONNECTOR') + bgSuffix,
                                connectors
                            )
                            ci += 2
                            continue
                        }
                    }

                    // Tree-Linien-Char → Batch (durchgehende Linie, kein Box-Char-Stroke)
                    const styleName = ((isCurrent && col.highlightStyle)
                        ? (col.highlightStyle || 'OUTLINE_CONNECTOR')
                        : (col.style || 'OUTLINE_CONNECTOR')) + bgSuffix
                    if (this._pushTreeCharSegments(
                        col.char, offsetX + col.col,
                        cellTopPx, cellBotPx, cy, cw, styleName,
                        connectors, horizontals, quarterH
                    )) {
                        continue
                    }

                    // Normaler Atlas-Char (Marker, Zahlen, Text)
                    const style = ((isCurrent && col.highlightStyle)
                        ? (Style[col.highlightStyle] || Style.OUTLINE_CONNECTOR)
                        : (Style[col.style] || Style.OUTLINE_CONNECTOR)) + bgSuffix
                    const charY = offsetY + fractionalY + markerShift
                    setCharFn(offsetX + col.col, charY, col.char, style)
                }
            }

            // Note-Text rendern
            if (outlinePaneWidth > 0 && i < noteLines.length) {
                const noteLine = noteLines[i]
                const noteStartCol = ev.getNoteStartCol(noteLine.paragraphId)
                const noteTextWidth = ev.getNoteTextWidth(noteLine.paragraphId)
                if (noteTextWidth > 0) {
                    this._renderNoteLine(
                        offsetX, offsetY + fractionalY, width,
                        noteStartCol, noteTextWidth,
                        noteLine, i, setCharFn, mainLine.heightQ || 4
                    )
                }
            }
        }

        renderer.drawBatchedConnectors(connectors, horizontals)
    }

    /**
     * Sammelt Baumlinien-Segmente durch den Spacing-Gap vor einer Zeile in die
     * übergebene Connector-Queue (kein direkter Stroke — wird gebatcht).
     */
    _renderSpacingColumns(offsetX, offsetY, width, lineIndex, renderer, connectors) {
        const ev = this.editorView
        const prevLineIndex = lineIndex - 1
        const prevColumns = ev._outlineColumns.get(prevLineIndex)
        if (!prevColumns) return

        // Aktuelle Zeile: welche Spalten haben vertikale Verbindung nach oben?
        const currColumns = ev._outlineColumns.get(lineIndex)
        const currColSet = new Set()
        if (currColumns) {
            for (const col of currColumns) {
                // Zeichen die eine Verbindung nach oben haben
                if (col.char === '│' || col.char === '├' || col.char === '┤' || col.char === '└' || col.char === '┘') {
                    currColSet.add(col.col)
                }
            }
        }

        // Pixel-Positionen: vom unteren Ende der prev-Zeile bis zum oberen
        // Rand der aktuellen Zeile. Wichtig: prevLineH, nicht charHeight — bei
        // Heading-Zeilen (lineH > charHeight) würde ein `+ charHeight` den
        // Gap-Start in die Arm-down-Region der prev-Zeile verschieben, also
        // in den Bereich den das Zeilen-Segment bei `│├┤` bereits abdeckt.
        // Safari kumuliert Shadow-Blur bei überlappenden Path-Segmenten →
        // Verdickung an heading-Zeilenenden (Chrome/Firefox: einmalig pro stroke).
        // Treibendes Grid (wie im Zeilen-Loop): der Gap-Connector schließt an die
        // Connector-Vertikale der Nachbarzeilen an, nicht ans kollabierte Note-Grid.
        const prevDrv = ev._outlineConnectorFieldAt(prevLineIndex)
        const currDrv = ev._outlineConnectorFieldAt(lineIndex)
        const prevPxY = ev._lineScreenPixelY(prevLineIndex, prevDrv)
        const prevLineH = ev._paneCellHeightQ(prevDrv, prevLineIndex) * (renderer.charHeight / 4)
        const basePx = offsetY * renderer.charHeight
        const spacingStartPx = basePx + prevPxY + prevLineH
        const spacingEndPx = basePx + ev._lineScreenPixelY(lineIndex, currDrv)

        // Nur vertikale Verbinder zeichnen wo beide Zeilen Verbindung haben.
        // Das (-)/(+)-Icon-Zentrum zählt als Quelle nach unten: bei getrennten
        // Kind-Absätzen (Icon-Zeile + Folge-Slot) muss der Gap überbrückt werden,
        // obwohl die Icon-Zeile kein │ trägt.
        for (const col of prevColumns) {
            if (col.col >= width) continue
            const isLineSource = (col.char === '│' || col.char === '├' || col.char === '┤')
            const isIconSource = (col.char === '-' || col.char === '+')
            if (isLineSource || isIconSource) {
                // Nur wenn aktuelle Zeile auch eine vertikale Verbindung an dieser Spalte hat
                if (!currColSet.has(col.col)) continue

                const isCurrent = this._isOutlineNodeCurrent(prevLineIndex, this._lastCurrentParagraphId)
                const bgSuffix = ev.lines[prevLineIndex].isBackground ? '+BG' : ''
                // Icon-Quelle: Connector-Style (das Icon trägt Marker-Styles, die
                // Gap-Linie soll aber wie die übrige Tree-Linie aussehen).
                const styleName = isIconSource
                    ? ((isCurrent ? 'OUTLINE_CURRENT' : 'OUTLINE_CONNECTOR') + bgSuffix)
                    : (((isCurrent && col.highlightStyle)
                        ? (col.highlightStyle || 'OUTLINE_CONNECTOR')
                        : (col.style || 'OUTLINE_CONNECTOR')) + bgSuffix)

                connectors.push({
                    col: offsetX + col.col,
                    fromPx: spacingStartPx,
                    toPx: spacingEndPx,
                    styleName
                })
            }
        }
    }

    /**
     * Rendert einen Bereich von Zeilen über lineIndex (für dirty-Range-Rendering).
     */
    _renderLinesForRange(offsetX, offsetY, width, height, fromLineIndex, toLineIndex, setCharFn) {
        const ev = this.editorView
        const outlineColumns = ev._outlineColumns
        const noteLines = ev.noteLines
        const outlinePaneWidth = ev.outlinePaneWidth
        const currentParagraphId = this._lastCurrentParagraphId

        if (!outlineColumns) return

        // Connector-Segmente (vertikal + horizontal) sammeln, am Ende als EIN Batch stroken
        const connectors = []
        const horizontals = []
        const renderer = ev.renderer
        const cw = renderer ? renderer.charWidth : 0
        const ch = renderer ? renderer.charHeight : 0

        for (let i = fromLineIndex; i < toLineIndex; i++) {
            if (i >= ev.lines.length) break
            const fractionalY = ev._lineToScreenY(i, 'note')
            const mainLine = ev.lines[i]

            if (mainLine.isSeparator) {
                this._renderSeparatorLine(offsetX, offsetY + fractionalY, width, mainLine, setCharFn)
                continue
            }

            const bgSuffix = mainLine.isBackground ? '+BG' : ''

            const lineColumns = outlineColumns.get(i)
            if (lineColumns && lineColumns.length > 0) {
                const isCurrent = this._isOutlineNodeCurrent(i, currentParagraphId)
                // Outline-Tree liegt im Note-Pane → Marker-Shift nach Note-Höhe (4QL → 0)
                const markerShift = this._markerShiftY(this.editorView.noteLines[i]?.heightQ || 4)
                const isPropRange = renderer && renderer.isProportional()

                // Connector-Geometrie folgt dem treibenden Pane (siehe
                // _renderLinesQuarterSpacing): └-Eckpunkt auf Mitte der letzten
                // echten Zeile, Vertikale über den ganzen Block.
                const drvField = renderer ? ev._outlineConnectorFieldAt(i) : 'text'
                const cellTopPx = renderer
                    ? (offsetY * ch + ev._lineScreenPixelY(i, drvField))
                    : (offsetY + fractionalY) * ch
                const connCellH = renderer ? ev._paneCellHeightQ(drvField, i) * (ch / 4) : 0
                const cellBotPx = cellTopPx + connCellH
                const cy = cellTopPx + connCellH / 2

                for (let ci = 0; ci < lineColumns.length; ci++) {
                    const col = lineColumns[ci]
                    if (col.col >= width) continue

                    // Proportional Mode: (-) Marker als Kreis-Icon
                    if (isPropRange && col.char === '(' && ci + 2 < lineColumns.length) {
                        const centerCol = lineColumns[ci + 1]
                        const closeCol = lineColumns[ci + 2]
                        if ((centerCol.char === '-' || centerCol.char === '+') && closeCol.char === ')') {
                            const isHovered = this._hoveredOutlineItemIcon === this._outlineItemIdAt(i)
                            const { borderStyle, symbolStyle } =
                                this._outlineIconStyles(isCurrent, isHovered, bgSuffix)
                            const iconY = offsetY + fractionalY + markerShift
                                + this._iconBaselineOffsetLines(mainLine.heightQ || 4)
                            renderer._renderControlIcon(
                                offsetX + centerCol.col, iconY,
                                centerCol.char, symbolStyle, borderStyle,
                                EDITOR.OUTLINE_NOTE_ICON_SCALE
                            )
                            // Brücke vom Icon-Unterrand bis zur Slot-Unterkante,
                            // wenn der Knoten weiterläuft (Umbruch ODER Kind-Absatz).
                            if (renderer) {
                                this._pushIconBridge(
                                    centerCol.col, offsetX, iconY, cellBotPx, i,
                                    (isCurrent ? 'OUTLINE_CURRENT' : 'OUTLINE_CONNECTOR') + bgSuffix,
                                    connectors
                                )
                            }
                            ci += 2
                            continue
                        }
                    }

                    // Tree-Linien-Char → Batch
                    const styleName = ((isCurrent && col.highlightStyle)
                        ? (col.highlightStyle || 'OUTLINE_CONNECTOR')
                        : (col.style || 'OUTLINE_CONNECTOR')) + bgSuffix
                    if (renderer && this._pushTreeCharSegments(
                        col.char, offsetX + col.col,
                        cellTopPx, cellBotPx, cy, cw, styleName,
                        connectors, horizontals, ch / 4
                    )) {
                        continue
                    }

                    // Normaler Atlas-Char
                    const style = ((isCurrent && col.highlightStyle)
                        ? (Style[col.highlightStyle] || Style.OUTLINE_CONNECTOR)
                        : (Style[col.style] || Style.OUTLINE_CONNECTOR)) + bgSuffix
                    const charY = offsetY + fractionalY + markerShift
                    setCharFn(offsetX + col.col, charY, col.char, style)
                }
            }

            if (outlinePaneWidth > 0 && i < noteLines.length) {
                const noteLine = noteLines[i]
                const noteStartCol = ev.getNoteStartCol(noteLine.paragraphId)
                const noteTextWidth = ev.getNoteTextWidth(noteLine.paragraphId)
                if (noteTextWidth > 0) {
                    this._renderNoteLine(
                        offsetX, offsetY + fractionalY, width,
                        noteStartCol, noteTextWidth,
                        noteLine, i, setCharFn, mainLine.heightQ || 4
                    )
                }
            }
        }

        if (renderer) renderer.drawBatchedConnectors(connectors, horizontals)
    }

    /**
     * Rendert eine einzelne Note-Zeile mit >< Markern.
     *
     * @param {number} offsetX - Absoluter X-Offset des Pane
     * @param {number} y - Absolute Y-Position
     * @param {number} paneWidth - Verfügbare Pane-Breite
     * @param {number} noteStartCol - Spalte wo > Marker beginnt
     * @param {number} noteTextWidth - Breite des Note-Textbereichs (ohne Marker)
     * @param {ScreenLine} noteLine - Die Note-ScreenLine
     * @param {Function} setCharFn - Render-Callback
     */
    _renderNoteLine(offsetX, y, paneWidth, noteStartCol, noteTextWidth, noteLine, noteLineIndex, setCharFn, mainHeightQ = 4) {
        // Padding-Zeilen: nichts rendern (Outline-Struktur wurde schon gerendert)
        if (noteLine.isPadding) return

        const para = this.model.getParagraph(noteLine.paragraphId)
        if (!para) return

        const bgSuffix = noteLine.isBackground ? '+BG' : ''

        // Outline-Selection: komplette Absätze werden selektiert
        const crossPaneSelected = this.editorView._isOutlineSelected(noteLine.paragraphId)

        // Proportional: Marker als 4QL-Glyphen rendern (System-Font, wie Note-Text)
        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()
        const MARKER_HQ = LINE_HEIGHT_Q.SECONDARY

        // Baseline-Alignment: Note-Marker an Hauptzeile ausrichten (wie Absatzmarker in Main)
        const markerBaselineShift = this._noteBaselineShiftPx(mainHeightQ)
        // Selektions-Rechtecke der Marker müssen denselben vertikalen Versatz tragen
        // wie Text- und End-Marker-Selektion in renderProportionalLine (selVertOffset),
        // sonst sitzt die >-Marker-Markierung zu hoch. Nur das Selektions-Rechteck,
        // nicht das Glyph (das wird über die eigene Baseline-Logik platziert).
        const markerSelVertOffset = isProp ? renderer._verticalPad * MARKER_HQ / 4 : 0

        // Leere Notes: >< anzeigen wenn Cursor dort ODER aktiver Absatz (immer)
        if (!para.note) {
            const cursor = this.model.cursor
            const cursorHere = cursor && cursor.field === 'note' && cursor.paragraphId === para.id
            const emptyActiveMarker = para.isOutlineItem() && noteLine.isFirstLine
            if ((cursorHere || emptyActiveMarker) && noteStartCol < paneWidth) {
                if (isProp) {
                    const mPx = (offsetX + noteStartCol) * renderer.charWidth
                    const mPy = y * renderer.charHeight + markerBaselineShift
                    const markerTextHeight = renderer.textHeightForHeightQ(MARKER_HQ)
                    if (crossPaneSelected) {
                        this.editorView._fillSelectionRect(renderer.ctx, mPx, mPy + markerSelVertOffset, renderer.markerWidthPx(MARKER_HQ), markerTextHeight)
                    }
                    renderer.renderProportionalLine(
                        renderer.ctx, '>', mPx, mPy,
                        [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                        null, null, null, MARKER_HQ
                    )
                    if (noteStartCol + 1 < paneWidth) {
                        const ePx = mPx + renderer.markerWidthPx(MARKER_HQ)
                        if (crossPaneSelected) {
                            this.editorView._fillSelectionRect(renderer.ctx, ePx, mPy + markerSelVertOffset, renderer.markerWidthPx(MARKER_HQ), markerTextHeight)
                        }
                        renderer.renderProportionalLine(
                            renderer.ctx, '<', ePx, mPy,
                            [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                            null, null, null, MARKER_HQ
                        )
                    }
                } else {
                    setCharFn(offsetX + noteStartCol, y, '>', this._markerStyle(para.id), crossPaneSelected)
                    if (noteStartCol + 1 < paneWidth) {
                        setCharFn(offsetX + noteStartCol + 1, y, '<', this._markerStyle(para.id), crossPaneSelected)
                    }
                }
            }
            return
        }

        // > Marker Position prüfen (muss innerhalb der Pane-Breite liegen)
        if (noteStartCol >= paneWidth) return

        // > Marker rendern (nur bei erster Zeile der Note)
        if (noteLine.isFirstLine) {
            if (isProp) {
                const mPx = (offsetX + noteStartCol) * renderer.charWidth
                const mPy = y * renderer.charHeight + markerBaselineShift
                if (crossPaneSelected) {
                    this.editorView._fillSelectionRect(renderer.ctx, mPx, mPy + markerSelVertOffset,
                        renderer.markerWidthPx(MARKER_HQ), renderer.textHeightForHeightQ(MARKER_HQ))
                }
                renderer.renderProportionalLine(
                    renderer.ctx, '>', mPx, mPy,
                    [{ start: 0, end: 1, bold: false, italic: false, underline: false, strikethrough: false, style: this._markerStyle(para.id) }],
                    null, null, null, MARKER_HQ
                )
            } else {
                setCharFn(offsetX + noteStartCol, y, '>', this._markerStyle(para.id), crossPaneSelected)
            }
        } else {
            setCharFn(offsetX + noteStartCol, y, ' ', Style.OUTLINE_NOTE + bgSuffix, crossPaneSelected)
        }

        // Per-Paragraph Note-Parsing (gecacht, Display-Indizes mit dataOffset)
        const fullParsed = this.editorView._getParsedRichText(this.editorView._noteParseCache, para.id, para.note, 'normal', 'note')
        const parsed = this.editorView._sliceParsedForLine(fullParsed, noteLine.startOffset, noteLine.endOffset)
        const highlightFn = this.editorView.searchHighlightFn

        // Proportional: pixelgenau rendern
        if (renderer && renderer.isProportional() && parsed.length > 0) {
            this._renderNoteLineProportional(
                offsetX, y, paneWidth, noteStartCol, noteTextWidth,
                noteLine, noteLineIndex, parsed, para, crossPaneSelected, highlightFn, renderer, setCharFn,
                markerBaselineShift
            )
            return
        }

        // Spellcheck-Wavy fuer Note (Mono-Pfad). Sammelt waehrend des Glyph-
        // Loops Grid-Ranges und zeichnet sie nach dem End-Marker. Pendant zum
        // Main-Pane-Loop in `EditorView._renderLineBody`.
        const noteWarnings = this.editorView._getEffectiveSpellWarnings(noteLine.paragraphId, 'note')
        const hasNoteWarnings = noteWarnings && noteWarnings.length > 0
        const spellRanges = []
        let curWavy = null

        let col = 0
        for (let i = 0; i < parsed.length && col < noteTextWidth; i++) {
            const charX = offsetX + noteStartCol + 1 + col
            if (charX - offsetX >= paneWidth) break

            const { char, style, dataOffset } = parsed[i]
            // Note-Text verwendet OUTLINE_NOTE als Basis-Style statt EDITOR_TEXT
            const noteStyle = ((style === Style.EDITOR_TEXT) ? Style.OUTLINE_NOTE : style) + bgSuffix

            // Selection-Highlighting: direkte Note-Selection ODER Cross-Pane (Main komplett selektiert) ODER Link-Selektion
            const isSelected = crossPaneSelected ||
                this.editorView._isPositionSelectedCached(noteLine.paragraphId, dataOffset, 'note') ||
                this.editorView._isLinkCharSelected(noteLine.paragraphId, 'note', parsed[i])

            // Such-Highlighting für Notes (verwende dataOffset für korrekte Position)
            const highlightType = highlightFn
                ? highlightFn(noteLine.paragraphId, dataOffset, 'note')
                : null
            setCharFn(charX, y, char, noteStyle, isSelected, highlightType)
            const dispW = charDisplayWidth(char)

            if (hasNoteWarnings) {
                let inWarn = false
                for (let wi = 0; wi < noteWarnings.length; wi++) {
                    const w = noteWarnings[wi]
                    if (dataOffset >= w.start && dataOffset < w.start + w.length) {
                        inWarn = true
                        break
                    }
                }
                if (inWarn) {
                    if (curWavy && curWavy.gridX + curWavy.gridW === charX) {
                        curWavy.gridW += dispW
                    } else {
                        curWavy = { gridX: charX, gridW: dispW }
                        spellRanges.push(curWavy)
                    }
                } else {
                    curWavy = null
                }
            }

            col += dispW
        }

        // < Marker am Ende (nur bei letzter Zeile der Note)
        if (noteLine.isLastLine) {
            const endX = offsetX + noteStartCol + 1 + col
            if (endX - offsetX < paneWidth) {
                setCharFn(endX, y, '<', this._markerStyle(para.id), crossPaneSelected)
            }
        }

        // Wavy-Underlines NACH allen Glyphen — additiv. Dim, wenn der Cursor
        // nicht in diesem Absatz steht.
        const renderer2 = this.editorView.renderer
        if (spellRanges.length > 0 && renderer2 && renderer2.drawSpellWavyMonospace) {
            const dim = !this.editorView.isLineActiveParagraph(noteLine)
            for (const r of spellRanges) {
                renderer2.drawSpellWavyMonospace(r.gridX, y, r.gridW, dim)
            }
        }
    }

    /**
     * Rendert eine Note-Zeile im Proportional-Modus.
     */
    _renderNoteLineProportional(offsetX, y, paneWidth, noteStartCol, noteTextWidth,
                                 noteLine, noteLineIndex, parsed, para, crossPaneSelected, highlightFn,
                                 renderer, setCharFn, baselineShift = 0) {
        const lineText = parsedCharsToString(parsed)
        let formatRuns = parsedToFormatRuns(parsed)

        // Background-Suffix an formatRun-Styles anhängen
        const bgSuffix = noteLine.isBackground ? '+BG' : ''
        if (bgSuffix) {
            formatRuns = formatRuns.map(run => ({ ...run, style: run.style + bgSuffix }))
        }

        // Spellcheck-Wavy fuer Note (Proportional-Pfad). Pendant zur Logik
        // in `EditorView._renderLineProportional`.
        const localSpellWarnings = this.editorView.buildLocalSpellWarnings(noteLine, 'note')
        if (localSpellWarnings) {
            formatRuns = enrichRunsWithSpellWarnings(
                formatRuns, localSpellWarnings, null,
                !this.editorView.isLineActiveParagraph(noteLine)
            )
        }

        const charWidth = renderer.charWidth
        const charHeight = renderer.charHeight
        const noteHeightQ = renderer.isProportional() ? LINE_HEIGHT_Q.SECONDARY : 4
        // Proportional: > Marker in 4QL (System-Font) reserviert markerWidthPx(SECONDARY)
        const textStartX = renderer.isProportional()
            ? (offsetX + noteStartCol) * charWidth + renderer.markerWidthPx(noteHeightQ)
            : (offsetX + noteStartCol + 1) * charWidth
        const pixelY = y * charHeight + baselineShift

        // Selection-Range berechnen
        let selRange = null
        let selStart = -1, selEnd = -1
        for (let i = 0; i < parsed.length; i++) {
            const isSel = crossPaneSelected ||
                this.editorView._isPositionSelectedCached(noteLine.paragraphId, parsed[i].dataOffset, 'note') ||
                this.editorView._isLinkCharSelected(noteLine.paragraphId, 'note', parsed[i])
            if (isSel && selStart < 0) selStart = i
            if (isSel) selEnd = i + 1
        }
        if (selStart >= 0) {
            selRange = { start: selStart, end: selEnd }
        }

        // Such-Highlighting VOR dem Text rendern (Hintergrund hinter Text)
        if (highlightFn) {
            const posMap = noteLineIndex >= 0 ? this.editorView._getPositionMap(noteLineIndex, 'note') : null
            if (posMap) {
                // Y/Höhe analog zur Selection in renderProportionalLine berechnen,
                // sonst rutscht der Highlight visuell nach oben (siehe selVertOffset
                // in Renderer.renderProportionalLine).
                const selVertOffset = renderer._verticalPad * noteHeightQ / 4
                const selPixelY = pixelY + selVertOffset
                const selHeight = renderer.textHeightForHeightQ(noteHeightQ)
                this._renderNoteSearchHighlights(
                    renderer, parsed, posMap, noteLine.paragraphId,
                    textStartX, selPixelY, selHeight, highlightFn
                )
            }
        }

        // End-Marker im proportionalen Fluss
        let endMarker = null
        if (noteLine.isLastLine) {
            endMarker = {
                char: '<',
                style: this._markerStyle(para.id),
                selected: crossPaneSelected,
                heightQ: noteHeightQ
            }
        }

        // Layout aus PositionMap wiederverwenden
        const noteLineMap = noteLineIndex >= 0 ? this.editorView._getPositionMap(noteLineIndex, 'note') : null
        const rawLayout = noteLineMap ? noteLineMap._rawLayout : null

        renderer.renderProportionalLine(
            renderer.ctx,
            lineText,
            textStartX,
            pixelY,
            formatRuns,
            selRange,
            endMarker,
            rawLayout,
            noteHeightQ
        )
    }

    /**
     * Rendert Such-Highlighting für eine proportionale Note-Zeile.
     * selPixelY/selHeight sind die vertikal zentrierten Werte (analog zur Selection),
     * nicht die nackte Zeilen-Top-Y plus volle charHeight.
     */
    _renderNoteSearchHighlights(renderer, parsed, posMap, paragraphId, textStartX, selPixelY, selHeight, highlightFn) {
        let hlStart = -1
        let hlType = null

        for (let i = 0; i <= parsed.length; i++) {
            const currentHl = i < parsed.length
                ? highlightFn(paragraphId, parsed[i].dataOffset, 'note')
                : null

            if (currentHl !== hlType) {
                if (hlType && hlStart >= 0) {
                    const startPx = textStartX + (hlStart < posMap.xPositions.length ? posMap.xPositions[hlStart] : posMap.totalWidth)
                    const endPx = textStartX + (i < posMap.xPositions.length ? posMap.xPositions[i] : posMap.totalWidth)

                    const bgColor = renderer._resolveSearchHighlightColor(hlType)
                    if (bgColor) {
                        renderer.ctx.fillStyle = bgColor
                        renderer.ctx.fillRect(startPx, selPixelY, endPx - startPx, selHeight)
                    }
                }
                hlStart = i
                hlType = currentHl
            }
        }
    }

    /**
     * Setzt den Hover-State für Separator-Controls.
     * @param {{ type: string, paragraphId: number }|null} control
     */
    setHoveredSeparatorControl(control) {
        const changed = !this._hoveredSeparatorControl !== !control ||
            (control && this._hoveredSeparatorControl &&
                (control.type !== this._hoveredSeparatorControl.type ||
                 control.paragraphId !== this._hoveredSeparatorControl.paragraphId))
        this._hoveredSeparatorControl = control
        if (changed) {
            this.editorView.isDirty = true
        }
        return changed
    }

    /**
     * Setzt den Hover-State für das Collapse-Icon eines Outline-Items.
     * @param {string|null} paragraphId
     * @returns {boolean} true, wenn sich der Hover-State geändert hat
     */
    setHoveredOutlineItemIcon(paragraphId) {
        if (this._hoveredOutlineItemIcon === paragraphId) return false
        this._hoveredOutlineItemIcon = paragraphId
        this.editorView.isDirty = true
        return true
    }

    /**
     * Liefert Border- und Symbol-Style für ein Outline-Item-Collapse-Icon.
     * Einheitliches Schema (exakt wie die Separator-Icons): Der Border folgt der
     * Linie — passiv die Connector-Farbe, aktiv die Active-Border-Color, wenn der
     * zugehörige Absatz den Cursor hat. Hover wechselt NUR das Symbol auf die
     * Hover-Farbe (der Border bleibt passiv/aktiv) — gleicher Hover wie bei den
     * Controls auf dem Separator.
     * @param {boolean} isCurrent  Absatz hat den Cursor
     * @param {boolean} isHovered  Maus über dem Icon
     * @param {string} bgSuffix    '+BG' bei Background-Items, sonst ''
     * @returns {{ borderStyle: string, symbolStyle: string }}
     */
    _outlineIconStyles(isCurrent, isHovered, bgSuffix) {
        return {
            borderStyle: (isCurrent ? Style.OUTLINE_CURRENT : Style.OUTLINE_CONNECTOR) + bgSuffix,
            symbolStyle: isHovered ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL
        }
    }

    /**
     * paragraphId des Outline-Items in Zeile lineIndex. Bei Co-Location (Spec
     * §3.2) steht das Outline-Item in noteLines, während lines den co-located
     * Text-Absatz trägt; ohne Co-Location sind beide identisch. An dieser ID
     * hängen Icon-Hover und das Highlight der Outline-Struktur.
     * @returns {string}
     */
    _outlineItemIdAt(lineIndex) {
        const note = this.editorView.noteLines[lineIndex]
        const line = this.editorView.lines[lineIndex]
        return (note && note.paragraphId) || (line && line.paragraphId) || null
    }

    /**
     * Ein Outline-Knoten gilt als aktuell, wenn der Cursor im Outline-Item ODER
     * im co-located Text-Absatz steht — beide teilen sich bei Co-Location dieselbe
     * Zeile und bilden visuell eine Einheit.
     * @returns {boolean}
     */
    _isOutlineNodeCurrent(lineIndex, currentParagraphId) {
        if (currentParagraphId == null) return false
        const line = this.editorView.lines[lineIndex]
        return this._outlineItemIdAt(lineIndex) === currentParagraphId
            || (line && line.paragraphId === currentParagraphId)
    }

    /**
     * Rendert eine strukturierte Separator-Zeile im Outline-Pane.
     * Erst durchgehende ─ Linie mit Controls, dann Label darüber mit Blanks.
     * Nummer immer, Titel nur wenn Main nicht sichtbar.
     */
    _renderSeparatorLine(offsetX, y, width, line, setCharFn) {
        const depth = line.separatorDepth
        const number = line.separatorNumber || ''
        const paragraphId = line.paragraphId
        const isBackgroundSep = depth === EDITOR.BACKGROUND_SEPARATOR_DEPTH
        const bgSuffix = line.isBackground ? '+BG' : ''
        // Linie + Icon-Borders teilen den Stil der Outline-Connector-Linie:
        // passiv die Connector-Farbe, aktiv (Cursor auf diesem Separator) die
        // Active-Border-Color. Gilt auch für den Background-Separator: dessen
        // Doppellinie trägt dieselbe Farbe wie normale Linien, nur Text bleibt sepia.
        const isActiveSep = this.editorView.model.cursor
            && this.editorView.model.cursor.paragraphId === paragraphId
        const hrStyle = isActiveSep ? Style.OUTLINE_CURRENT : Style.OUTLINE_CONNECTOR
        const lineChar = isBackgroundSep ? '═' : '─'

        // Titel nur anzeigen wenn Main nicht sichtbar
        const mainHidden = this.editorView.mainPaneWidth <= 0

        // Background-Separator: spezielles Layout — nur (-) Control, kein (<|>|=)
        // Renderer für Icon-Controls im Proportional Mode
        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()

        // Collapse-Icon-Zeichen: (+) wenn sectionCollapsed, sonst (-)
        const sepPara = this.editorView.model.getParagraph(paragraphId)
        const collapseChar = (sepPara && sepPara.sectionCollapsed) ? '+' : '-'

        if (isBackgroundSep) {
            // (-)/(+) Control am Anfang, dann ═══ bis zum Ende
            let pos = 0
            if (pos + 3 <= width) {
                const minusStyle = (this._hoveredSeparatorControl &&
                    this._hoveredSeparatorControl.paragraphId === paragraphId &&
                    this._hoveredSeparatorControl.type === 'collapse')
                    ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL
                if (isProp) {
                    renderer._renderControlIcon(offsetX + pos + 1, y, collapseChar, minusStyle, hrStyle)
                } else {
                    setCharFn(offsetX + pos, y, '(', hrStyle)
                    setCharFn(offsetX + pos + 1, y, collapseChar, minusStyle)
                    setCharFn(offsetX + pos + 2, y, ')', hrStyle)
                }
                pos += 3
            }
            while (pos < width) {
                setCharFn(offsetX + pos, y, lineChar, hrStyle); pos++
            }

            // Label nur anzeigen wenn Main nicht sichtbar (Priorität: Main > Outline > Context)
            if (!mainHidden) return

            // Label: "Background · Dokumenttitel"
            const docTitle = this.editorView.model.metadata && this.editorView.model.metadata.title
            const labelText = docTitle ? `Background · ${docTitle}` : 'Background'
            const labelCol = 4  // Nach (-)═

            if (isProp) {
                const charWidth = renderer.charWidth
                const charHeight = renderer.charHeight
                const pixelY = y * charHeight
                const formatRuns = [{
                    start: 0, end: labelText.length,
                    bold: true, italic: false, underline: false, strikethrough: false,
                    style: Style.SEPARATOR_TITLE + bgSuffix
                }]
                const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
                const layout = computeStringLayout(labelText, activeCache, activeCache.baseStep, formatRuns)
                const labelWidthPx = layout.totalWidth * activeSF
                const labelStartPx = (offsetX + labelCol + 1) * charWidth
                const labelBgW = charWidth + labelWidthPx + charWidth
                renderer.ctx.fillStyle = renderer.colors.background
                renderer.ctx.fillRect(labelStartPx - charWidth, pixelY, labelBgW, charHeight)
                const maxWidthPx = (offsetX + width) * charWidth - labelStartPx - charWidth
                if (maxWidthPx > 0) {
                    renderer.renderProportionalLine(
                        renderer.ctx, labelText, labelStartPx, pixelY, formatRuns, null, null, null
                    )
                }
            } else {
                const maxLen = width - labelCol - 2
                if (maxLen > 0) {
                    const display = labelText.length > maxLen ? labelText.slice(0, maxLen) : labelText
                    if (renderer) {
                        const charWidth = renderer.charWidth
                        const charHeight = renderer.charHeight
                        const bgX = (offsetX + labelCol) * charWidth
                        const bgW = (display.length + 2) * charWidth
                        renderer.ctx.fillStyle = renderer.colors.background
                        renderer.ctx.fillRect(bgX, y * charHeight, bgW, charHeight)
                    }
                    let col = labelCol
                    setCharFn(offsetX + col++, y, ' ', hrStyle)
                    for (let i = 0; i < display.length && col < width - 1; i++) {
                        setCharFn(offsetX + col++, y, display[i], Style.SEPARATOR_TITLE + bgSuffix)
                    }
                    if (col < width) setCharFn(offsetX + col, y, ' ', hrStyle)
                }
            }
            return
        }

        const title = mainHidden ? (line.separatorTitle || '') : ''

        // Lock-Control nur in Outline rendern, wenn weder Main noch Context
        // sichtbar sind (Priorität: Main > Context > Outline).
        const contextHidden = this.editorView.contextPaneWidth <= 0
        const showLockHere = mainHidden && contextHidden

        // Controls rechts: (<|>|=)─ = 8 Zeichen, mit Lock zusätzlich +7 (3 Striche
        // Abstand + (v) + 1 Strich rechts = 15 gesamt).
        const controlsWidth = showLockHere ? 15 : 8
        const controlStartX = width - controlsWidth

        // Zu wenig Platz → einfache ─ Linie
        if (controlStartX < 4) {
            for (let i = 0; i < width; i++) {
                setCharFn(offsetX + i, y, lineChar, hrStyle)
            }
            return
        }

        // Hover-State
        const hovered = this._hoveredSeparatorControl
        const isHoveredHere = hovered && hovered.paragraphId === paragraphId
        const ltActive = depth > 0
        const gtActive = depth < EDITOR.MAX_SEPARATOR_DEPTH
        const ltStyle = ltActive
            ? (isHoveredHere && hovered.type === 'depth_decrease' ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL)
            : hrStyle
        const gtStyle = gtActive
            ? (isHoveredHere && hovered.type === 'depth_increase' ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL)
            : hrStyle
        const eqStyle = isHoveredHere && hovered.type === 'insert_separator'
            ? Style.CONTROL_HOVER
            : Style.CONTROL_NORMAL
        const lockStyle = isHoveredHere && hovered.type === 'lock'
            ? Style.CONTROL_HOVER
            : Style.CONTROL_NORMAL
        const locked = showLockHere
            ? this.editorView.model.isSectionLocked(paragraphId)
            : false
        const lockChar = locked ? 'v' : ' '

        // 1. Durchgehende Linie mit Einrückung und Controls
        let pos = 0
        const indent = depth * 2
        for (let i = 0; i < indent && pos < controlStartX; i++) {
            setCharFn(offsetX + pos, y, ' ', hrStyle); pos++
        }
        // (-)/(+) Collapse-Marker (links) — mit Hover-State
        if (pos + 3 <= controlStartX) {
            const collapseHovered = isHoveredHere && hovered.type === 'collapse'
            const collapseStyle = collapseHovered ? Style.CONTROL_HOVER : Style.CONTROL_NORMAL
            if (isProp) {
                renderer._renderControlIcon(offsetX + pos + 1, y, collapseChar, collapseStyle, hrStyle)
            } else {
                setCharFn(offsetX + pos, y, '(', hrStyle)
                setCharFn(offsetX + pos + 1, y, collapseChar, collapseStyle)
                setCharFn(offsetX + pos + 2, y, ')', hrStyle)
            }
            pos += 3
        }
        // ─ Füllung bis Controls
        while (pos < controlStartX) {
            setCharFn(offsetX + pos, y, lineChar, hrStyle); pos++
        }
        // Controls (<|>|=)
        if (isProp) {
            // Drei berührende Kreis-Icons (Zentren 2 Zellen Abstand)
            renderer._renderControlIcon(offsetX + pos + 1, y, '<', ltStyle, hrStyle)
            pos += 2
            renderer._renderControlIcon(offsetX + pos + 1, y, '>', gtStyle, hrStyle)
            pos += 2
            renderer._renderControlIcon(offsetX + pos + 1, y, '=', eqStyle, hrStyle)
            pos += 3
        } else {
            setCharFn(offsetX + pos, y, '(', hrStyle); pos++
            setCharFn(offsetX + pos, y, '<', ltStyle); pos++
            setCharFn(offsetX + pos, y, '|', hrStyle); pos++
            setCharFn(offsetX + pos, y, '>', gtStyle); pos++
            setCharFn(offsetX + pos, y, '|', hrStyle); pos++
            setCharFn(offsetX + pos, y, '=', eqStyle); pos++
            setCharFn(offsetX + pos, y, ')', hrStyle); pos++
        }
        // Wenn Lock hier sichtbar: ─ Spacer, dann (v)/( ) am rechten Rand
        if (showLockHere) {
            // Spacer-Striche bis Lock-Position (lockX = width - 4)
            while (pos < width - 4) {
                setCharFn(offsetX + pos, y, lineChar, hrStyle); pos++
            }
            if (isProp) {
                renderer._renderControlIcon(offsetX + pos + 1, y, lockChar, lockStyle, hrStyle)
                pos += 3
            } else {
                setCharFn(offsetX + pos, y, '(', hrStyle); pos++
                setCharFn(offsetX + pos, y, lockChar, lockStyle); pos++
                setCharFn(offsetX + pos, y, ')', hrStyle); pos++
            }
        }
        while (pos < width) {
            setCharFn(offsetX + pos, y, lineChar, hrStyle); pos++
        }

        // 2. Label darüber legen (nach Einrückung + Marker)
        // Marker-Logik: Outline-Marker erscheinen nur wenn Main hidden ist (dann
        // ist hier das aktive Edit-Pane fuer Separator-Titel). Marker brauchen
        // sepState fuer den Active/Dim-Style.
        const sepState = mainHidden && this.editorView._separatorInputAccessor
            ? this.editorView._separatorInputAccessor() : null
        const usableSepState = sepState
            && sepState.pane === 'outline'
            && this.editorView.model.cursor
            && this.editorView.model.cursor.paragraphId === paragraphId
            ? sepState : null
        const labelCol = indent + 4  // Nach Einrückung + (-)─
        // Marker brauchen: > (1) + < (1) = 2 Chars (leer) bzw. mehr mit Label.
        // Bei leerem Label: Marker bei labelCol-1 und labelCol — muss < controlStartX bleiben.
        const innerFits = (controlStartX - labelCol) >= 1
        const markerStyle = (mainHidden && innerFits)
            ? pickSeparatorMarkerStyle(MarkerPane.OUTLINE, paragraphId,
                this.editorView.model.cursor, usableSepState)
            : null

        if (!number && !title && !markerStyle) return

        // Label zusammenbauen: "Nummer Titel" oder nur "Nummer"
        let labelText = number
        if (title) {
            if (number) labelText += ' '
            labelText += title
        }
        if (!labelText && !markerStyle) return

        // Nummer in normaler Textfarbe (nicht in der Linien-Farbe), Titel bold
        // in normaler Textfarbe via SEPARATOR_TITLE.
        const labelHrStyle = Style.EDITOR_TEXT + bgSuffix
        const labelTitleStyle = Style.SEPARATOR_TITLE + bgSuffix
        const overlayBg = renderer && renderer.colors ? renderer.colors.background : null

        if (isProp) {
            const charWidth = renderer.charWidth
            const charHeight = renderer.charHeight
            const pixelY = y * charHeight
            const isEmpty = labelText.length === 0 && markerStyle

            const formatRuns = []
            if (number) {
                formatRuns.push({
                    start: 0, end: number.length,
                    bold: false, italic: false, underline: false, strikethrough: false,
                    style: labelHrStyle
                })
            }
            if (title) {
                const titleStart = number ? number.length + 1 : 0
                formatRuns.push({
                    start: titleStart, end: labelText.length,
                    bold: true, italic: false, underline: false, strikethrough: false,
                    style: labelTitleStyle
                })
            }

            // Label-Breite berechnen
            const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
            const layout = computeStringLayout(labelText, activeCache, activeCache.baseStep, formatRuns)
            const labelWidthPx = layout.totalWidth * activeSF
            // Prop-Mode: 1 Cell weiter rechts, sonst beruehrt der > Marker das
            // (-) Icon und es entsteht ein 1-Pixel-Aliasing zwischen den beiden.
            // Outline ist maximiert wenn der Edit hier passiert — Platz reicht.
            const labelStartPx = (offsetX + labelCol + 1) * charWidth
            // Marker passen sich der Linien-Hoehe an (kleiner auf Separator).
            // Glyph-Box ebenfalls proportional, sonst Luecke zwischen Glyph
            // und Label (Glyph wird linksbuendig in Box gemalt).
            const lineHQ = line.heightQ || 4
            const markerHQ = lineHQ
            const markerWidthPx = renderer.markerWidthPx() * lineHQ / LINE_HEIGHT_Q.NORMAL

            // BG-Block ueber > Label < damit die ─ Linie nicht durch die Marker
            // schimmert. Bei isEmpty: nur >< Marker. Ohne Marker (Display-Mode
            // der Kapitelnummern, wenn Outline nicht das Edit-Pane ist): Spacer
            // links/rechts statt Marker, damit die Linie nicht direkt ans Label
            // stoesst.
            if (markerStyle || !isEmpty) {
                const sideWidth = markerStyle ? markerWidthPx : charWidth
                const startPx = labelStartPx - sideWidth
                const totalPx = sideWidth + labelWidthPx + sideWidth
                renderer.ctx.fillStyle = overlayBg
                renderer.ctx.fillRect(startPx, pixelY, totalPx, charHeight)
            }

            // Marker > / < links/rechts (nur wenn Outline Edit-Pane ist)
            if (markerStyle) {
                // markerHQ === lineHQ → Glyph-Mitte = pixelY + charHeight/2,
                // gleiche Position wie ─ Linie. Kein Vertikal-Shift noetig.
                const markerPixelY = pixelY
                const startMarkerPx = labelStartPx - markerWidthPx
                const endMarkerPx = labelStartPx + labelWidthPx
                const markerRuns = [{
                    start: 0, end: 1, bold: false, italic: false,
                    underline: false, strikethrough: false, style: markerStyle
                }]
                renderer.renderProportionalLine(
                    renderer.ctx, '>', startMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
                renderer.renderProportionalLine(
                    renderer.ctx, '<', endMarkerPx, markerPixelY,
                    markerRuns, null, null, null, markerHQ
                )
            }

            // Proportionalen Text darüber (nur wenn nicht leer)
            const maxWidthPx = (offsetX + controlStartX) * charWidth - labelStartPx - charWidth
            if (maxWidthPx > 0 && labelText.length > 0) {
                renderer.renderProportionalLine(
                    renderer.ctx, labelText, labelStartPx, pixelY, formatRuns, null, null, null
                )
            }
        } else {
            // Monospace: > Label < direkt (kein Spacer)
            const markerReserve = markerStyle ? 1 : 0
            const maxLen = controlStartX - labelCol - 2 * markerReserve
            if (maxLen <= 0 && !markerStyle) return
            const display = (labelText && maxLen > 0)
                ? (labelText.length > maxLen ? labelText.slice(0, maxLen) : labelText)
                : ''
            const isEmpty = display.length === 0 && markerStyle

            // BG-Block ueber > Label < damit die ─ Linie nicht durch die Marker
            // schimmert. Outline-Sonderfall hat immer mind. die Number, also
            // ist isEmpty hier praktisch nie true — aber Code deckt es ab.
            if (renderer && (markerStyle || !isEmpty)) {
                const charWidth = renderer.charWidth
                const charHeight = renderer.charHeight
                const startCol = markerStyle ? labelCol - 1 : labelCol
                const totalCols = (markerStyle ? 1 : 0)
                    + (isEmpty ? 0 : display.length)
                    + (markerStyle ? 1 : 0)
                renderer.ctx.fillStyle = overlayBg
                renderer.ctx.fillRect((offsetX + startCol) * charWidth, y * charHeight,
                    totalCols * charWidth, charHeight)
            }

            if (isEmpty) {
                // ><  direkt aneinander
                if (markerStyle) {
                    setCharFn(offsetX + labelCol - 1, y, '>', markerStyle)
                    setCharFn(offsetX + labelCol, y, '<', markerStyle)
                }
            } else {
                if (markerStyle) {
                    setCharFn(offsetX + labelCol - 1, y, '>', markerStyle)
                }

                let col = labelCol
                for (let i = 0; i < display.length && col < controlStartX - 1; i++) {
                    const isInTitle = title && i >= (number ? number.length + 1 : 0)
                    setCharFn(offsetX + col++, y, display[i], isInTitle ? labelTitleStyle : labelHrStyle)
                }

                if (markerStyle && col < controlStartX) {
                    setCharFn(offsetX + col, y, '<', markerStyle)
                }
            }
        }
    }

    /**
     * Prüft ob localX/localY auf das (-)/(+) Collapse-Icon eines aktiven Absatzes
     * im Outline-Pane fällt. Liest das Ergebnis aus _outlineColumns und sucht
     * die Spalte mit char '-' oder '+'.
     * @returns {number|null} paragraphId oder null
     */
    getActiveCollapseIconParagraphId(localX, localY) {
        const lineIndex = this.editorView.screenYToLineIndex(localY, 'note')
        // Outline-Items werden im Main-Pane übersprungen (Co-Location-Rendering),
        // daher gegen noteLines prüfen — dieselbe Index-Basis wie _outlineColumns.
        const noteLines = this.editorView.noteLines
        if (lineIndex < 0 || lineIndex >= noteLines.length) return null
        const line = noteLines[lineIndex]
        if (!line || line.isSeparator || !line.isFirstLine) return null

        const para = this.editorView.model.getParagraph(line.paragraphId)
        if (!para || !para.isOutlineItem()) return null

        const cols = this.editorView._outlineColumns.get(lineIndex)
        if (!cols) return null
        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()
        for (const c of cols) {
            if (c.char !== '-' && c.char !== '+') continue
            // Icon-Hit: proportional 3 Zellen breit um das Zentrum
            if (isProp) {
                if (localX >= c.col - 1 && localX <= c.col + 1) return line.paragraphId
            } else {
                if (localX === c.col) return line.paragraphId
            }
        }
        return null
    }

    /**
     * Prüft ob an der gegebenen Position ein Separator-Control liegt.
     * @param {number} localX - X-Position relativ zum Outline-Pane-Inneren
     * @param {number} localY - Y-Position relativ zum Outline-Pane-Inneren
     * @returns {{ type: string, paragraphId: number }|null}
     */
    getSeparatorControlAtPosition(localX, localY) {
        const lineIndex = this.editorView.screenYToLineIndex(localY, 'note')
        const lines = this.editorView.lines

        if (lineIndex >= lines.length) return null

        const line = lines[lineIndex]
        if (!line.isSeparator) return null

        const width = this.editorView.outlinePaneWidth
        if (width <= 0) return null

        const depth = line.separatorDepth

        // Proportional Mode: Icon-Controls 3 Zellen breit
        const renderer = this.editorView.renderer
        const isProp = renderer && renderer.isProportional()

        // Background-Separator: nur (-) Control am Anfang (Zentrum Position 1)
        if (depth === EDITOR.BACKGROUND_SEPARATOR_DEPTH) {
            if (isProp ? (localX >= 0 && localX <= 2) : localX === 1) {
                return { type: 'collapse', paragraphId: line.paragraphId }
            }
            return null
        }

        // (-)/(+) Collapse-Control: links nach Einrückung (col indent+1 = Icon-Zentrum)
        const indent = depth * 2
        if (isProp) {
            if (localX >= indent && localX <= indent + 2) {
                return { type: 'collapse', paragraphId: line.paragraphId }
            }
        } else {
            if (localX === indent + 1) {
                return { type: 'collapse', paragraphId: line.paragraphId }
            }
        }

        // Controls Position: width - 8 (oder width - 15 wenn Lock hier sichtbar)
        const contextHidden = this.editorView.contextPaneWidth <= 0
        const mainHidden = this.editorView.mainPaneWidth <= 0
        const showLockHere = mainHidden && contextHidden
        const controlsWidth = showLockHere ? 15 : 8
        const controlStartX = width - controlsWidth
        if (controlStartX < 4) return null

        // (<|>|=)─ Layout:
        // controlStartX+0: (    controlStartX+1: <    controlStartX+2: |
        // controlStartX+3: >    controlStartX+4: |    controlStartX+5: =
        // controlStartX+6: )    controlStartX+7: ─
        // Mit Lock zusätzlich:
        // width-5: (   width-4: v/space   width-3: )   width-2..-1: ─

        if (isProp) {
            // Icon-Zentren: <@+1, >@+3, =@+5 — je ±1 Zelle Hit-Bereich
            if (localX >= controlStartX && localX <= controlStartX + 2 && depth > 0) {
                return { type: 'depth_decrease', paragraphId: line.paragraphId }
            }
            if (localX >= controlStartX + 2 && localX <= controlStartX + 4 && depth < EDITOR.MAX_SEPARATOR_DEPTH) {
                return { type: 'depth_increase', paragraphId: line.paragraphId }
            }
            if (localX >= controlStartX + 4 && localX <= controlStartX + 6) {
                return { type: 'insert_separator', paragraphId: line.paragraphId }
            }
            if (showLockHere && localX >= width - 5 && localX <= width - 3) {
                return { type: 'lock', paragraphId: line.paragraphId }
            }
        } else {
            if (localX === controlStartX + 1 && depth > 0) {
                return { type: 'depth_decrease', paragraphId: line.paragraphId }
            }
            if (localX === controlStartX + 3 && depth < EDITOR.MAX_SEPARATOR_DEPTH) {
                return { type: 'depth_increase', paragraphId: line.paragraphId }
            }
            if (localX === controlStartX + 5) {
                return { type: 'insert_separator', paragraphId: line.paragraphId }
            }
            if (showLockHere && localX >= width - 5 && localX <= width - 3) {
                return { type: 'lock', paragraphId: line.paragraphId }
            }
        }

        return null
    }

    /**
     * Berechnet den Zeichenoffset im Separator-Titel für einen Outline-Klick.
     * Berücksichtigt Einrückung, (-)-Marker und Nummer vor dem Titel.
     * @param {number} localX - X-Position relativ zum Outline-Pane-Inneren
     * @param {Object} line - ScreenLine
     * @returns {number} Zeichenoffset im Titel
     */
    getSeparatorTitleCharOffset(localX, line, pixelX) {
        const depth = line.separatorDepth
        const number = line.separatorNumber || ''
        const indent = depth * 2
        const labelCol = indent + 4  // indent + (-)─

        const renderer = this.editorView.renderer
        if (renderer && renderer.isProportional() && renderer._metricsCache && pixelX !== undefined) {
            // Proportional: pixelX → Zeichenposition via Layout
            const charWidth = renderer.charWidth
            const title = line.separatorTitle || ''

            // labelText + formatRuns identisch zum Rendering aufbauen
            let labelText = number
            if (title) {
                if (number) labelText += ' '
                labelText += title
            }
            if (!labelText) return 0

            // Prop: 1 Cell Offset (analog zum Render-Pfad — Anti-Aliasing-Spacer
            // zum (-) Collapse-Icon).
            const labelStartPx = (labelCol + 1) * charWidth
            const relPx = pixelX - labelStartPx
            if (relPx <= 0) return 0

            const bgSuffix = line.isBackground ? '+BG' : ''
            const formatRuns = []
            if (number) {
                formatRuns.push({ start: 0, end: number.length, bold: false, italic: false, style: Style.EDITOR_HR + bgSuffix })
            }
            if (title) {
                const titleStart = number ? number.length + 1 : 0
                formatRuns.push({ start: titleStart, end: labelText.length, bold: true, italic: false, style: Style.SEPARATOR_TITLE + bgSuffix })
            }

            const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
            const layout = computeStringLayout(labelText, activeCache, activeCache.baseStep, formatRuns)
            const scaledRelPx = relPx / activeSF

            // Nummer-Zeichen überspringen, nur Titel-Offset zurückgeben
            const titleOffset = number ? number.length + 1 : 0
            const codepoints = Array.from(labelText)
            for (let i = titleOffset; i < codepoints.length; i++) {
                const charRight = layout.positions[i] + layout.widths[i]
                const charMid = layout.positions[i] + layout.widths[i] / 2
                if (scaledRelPx < charMid) return i - titleOffset
            }
            return Math.max(0, codepoints.length - titleOffset)
        }

        // Monospace: Grid-basierte Berechnung
        // labelCol (kein Spacer) + number + 1 (Space innerhalb labelText) = Titel-Start
        const titleStart = labelCol + (number.length > 0 ? number.length + 1 : 0)
        return Math.max(0, localX - titleStart)
    }

    /**
     * Berechnet die pixelX-Position des Separator-Cursors im Outline-Pane.
     * Für proportionales Rendering — berücksichtigt Nummer (nicht-bold) und Titel (bold).
     * @param {number} displayCursorPos - Cursor-Position im sichtbaren Titel (nach scrollOffset)
     * @param {Object} line - ScreenLine
     * @param {string} title - Sichtbarer Titel-Text (nach scrollOffset)
     * @returns {number|undefined} pixelX oder undefined (Monospace)
     */
    getSeparatorCursorPixelX(displayCursorPos, line, title) {
        const renderer = this.editorView.renderer
        if (!renderer || !renderer.isProportional()) return undefined

        const charWidth = renderer.charWidth
        const depth = line.separatorDepth
        const number = line.separatorNumber || ''
        const indent = depth * 2
        const labelCol = indent + 4  // indent + (-)─

        // Exakt denselben labelText + formatRuns aufbauen wie im Rendering
        let labelText = number
        if (title) {
            if (number) labelText += ' '
            labelText += title
        }
        if (!labelText) labelText = ''

        const hrStyle = Style.EDITOR_HR + (line.isBackground ? '+BG' : '')
        const titleStyle = Style.SEPARATOR_TITLE + (line.isBackground ? '+BG' : '')
        const formatRuns = []
        if (number) {
            formatRuns.push({
                start: 0, end: number.length,
                bold: false, italic: false, underline: false, strikethrough: false,
                style: hrStyle
            })
        }
        if (title) {
            const titleStart = number ? number.length + 1 : 0
            formatRuns.push({
                start: titleStart, end: labelText.length,
                bold: true, italic: false, underline: false, strikethrough: false,
                style: titleStyle
            })
        }

        // Prop: 1 Cell Offset (analog zum Render-Pfad — Anti-Aliasing-Spacer
        // zum (-) Collapse-Icon).
        const labelStartPx = (labelCol + 1) * charWidth

        if (labelText.length === 0) return labelStartPx

        // Gesamten labelText als einen String vermessen (identisch zum Rendering)
        const { cache: activeCache, scaleFactor: activeSF } = renderer._activeMetrics()
        const layout = computeStringLayout(labelText, activeCache, activeCache.baseStep, formatRuns)

        // Cursor-Position im Titel-Teil: Nummer-Länge + Space überspringen
        const titleOffset = number ? number.length + 1 : 0
        const cpIndex = titleOffset + displayCursorPos
        const posInLabel = cpIndex < layout.positions.length
            ? layout.positions[cpIndex] * activeSF
            : layout.totalWidth * activeSF

        return labelStartPx + posInLabel
    }
}
